import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FamilyDiseaseInformation } from '@app/modules/service-data/types/service-data.types';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableConfigurationColumn,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { controlContainerProvider } from '@shared/helpers';
import { concat, defer, first, of, tap } from 'rxjs';

@Component({
  selector: 'app-service-data-form-disease-history',
  imports: [
    NgClass,
    DataTableComponent,
    ValidationErrorComponent,
    NgbTooltip,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './service-data-form-disease-history.component.html',
  viewProviders: [controlContainerProvider],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDataFormDiseaseHistoryComponent implements OnInit {
  private controlContainer = inject(ControlContainer);
  private cdRef = inject(ChangeDetectorRef);

  @Input() relationToPatient = false;
  @Input() ageOfOnset = false;
  @Input({ required: true }) translationGroup = '';
  @Input({ required: true }) fieldSet: string[];
  @Input({ required: true }) field: string;

  protected get form() {
    return this.controlContainer.control as FormGroup;
  }

  protected diseaseConfiguration: DataTableConfiguration;
  protected conditionDescriptionFormControl = new FormControl<string>('', [
    Validators.required,
  ]);
  protected relationToPatientFormControl = new FormControl<string>('', [
    Validators.required,
  ]);
  protected ageOfOnsetFormControl = new FormControl<string>(null, [
    Validators.min(0),
    Validators.max(200),
    Validators.required,
  ]);

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const rows = concat(
      defer(() => of(this.form.get('diseases').value)).pipe(first()),
      this.form.get('diseases').valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.cdRef.markForCheck())
      )
    );

    const columns: DataTableConfigurationColumn[] = [
      {
        label: 'Disease',
        property: 'conditionDescription',
      },
    ];

    if (this.relationToPatient) {
      columns.push({
        label: 'Relation to patient',
        property: 'relationToPatient',
      });
    }

    if (this.ageOfOnset) {
      columns.push({
        label: 'Age of onset',
        property: 'ageOfOnset',
      });
    }

    this.diseaseConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'serviceData.form.tooltips.removeDisease',
          action: this.removeDisease.bind(this),
          icon: 'fa-light fa-xmark',
          type: DataTableActionType.BUTTON,
        },
      ],
      columns,
      rows$: rows,
    };

    this.form
      .get('hasHistory')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: boolean) => {
        if (!value) {
          this.form.get('diseases').reset([]);
        }
      });
  }

  protected addDisease(): void {
    if (
      !this.conditionDescriptionFormControl.valid &&
      (!this.relationToPatientFormControl.valid || this.relationToPatient) &&
      (!this.ageOfOnsetFormControl.valid || this.ageOfOnset)
    ) {
      return;
    }
    const formValue: any = {
      conditionDescription: this.conditionDescriptionFormControl.value,
    };

    if (this.relationToPatient) {
      formValue.relationToPatient = this.relationToPatientFormControl.value;
    }

    if (this.ageOfOnset) {
      formValue.ageOfOnset = this.ageOfOnsetFormControl.value;
    }

    this.form
      .get('diseases')
      .patchValue([...this.form.get('diseases').value, formValue]);

    this.conditionDescriptionFormControl.reset();
    this.relationToPatientFormControl.reset();
    this.ageOfOnsetFormControl.reset();
  }

  protected isAddButtonDisabled(): boolean {
    return (
      !this.conditionDescriptionFormControl.valid ||
      (!this.relationToPatientFormControl.valid && this.relationToPatient) ||
      (!this.ageOfOnsetFormControl.valid && this.ageOfOnset)
    );
  }

  protected getColumnClass(): string {
    if (this.relationToPatient && this.ageOfOnset) {
      return 'col-12 col-md-4';
    }

    if (this.relationToPatient || this.ageOfOnset) {
      return 'col-12 col-md-6';
    }

    return 'col-12';
  }

  private removeDisease(
    event: MouseEvent,
    diseaseHistory: FamilyDiseaseInformation
  ): void {
    event.preventDefault();

    const diseaseControl = this.form.get('diseases');

    diseaseControl.patchValue(
      diseaseControl.value.filter(
        (dh: FamilyDiseaseInformation) =>
          dh?.conditionDescription !== diseaseHistory?.conditionDescription
      )
    );
  }
}
